"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCorpus = void 0;
// Required to use top level await
require("regenerator-runtime/runtime");
require("../../../i18n/i18n");
const papaparse_1 = __importDefault(require("papaparse"));
const toNumber_1 = __importDefault(require("lodash/toNumber"));
const stringToNumArray_1 = require("./stringToNumArray");
const camelize_1 = require("./camelize");
const randomizeStimulusBlocks_1 = require("./randomizeStimulusBlocks");
const shuffleRoarInferenceStories_1 = require("../../roar-inference/helpers/shuffleRoarInferenceStories");
const taskStore_1 = require("../../../taskStore");
const getBucketName_1 = require("./getBucketName");
const childSurveyResponses_1 = require("./childSurveyResponses");
const shouldUseClowder_1 = require("./shouldUseClowder");
let totalTrials = 0;
let totalDownexTrials = 0;
let stimulusData = [];
let downexData = [];
function writeItem(row) {
    if (row.task === 'math' && row.trial_type.includes('Number Line')) {
        const splitArr = row.item.split(',');
        return splitArr.map((el) => (0, toNumber_1.default)(el));
    }
    return row.item;
}
function containsLettersOrSlash(str) {
    return /[a-zA-Z\/]/.test(str);
}
const transformCSV = (csvInput, sequentialStimulus, task) => {
    let currTrialTypeBlock = '';
    let currPracticeAmount = 0;
    const blockThresholds = [];
    csvInput.forEach((row) => {
        // Leaving this here for quick testing of a certain type of trial
        // if (!row.trial_type.includes('Number Line')) return;
        var _a, _b, _c;
        if (row.block_threshold && !blockThresholds.includes(row.block_threshold)) {
            blockThresholds.push(row.block_threshold);
        }
        const newRow = {
            source: row.source,
            block_index: parseInt(row.block_index),
            task: row.task,
            // for testing, will be removed
            prompt: row.prompt,
            item: writeItem(row),
            origItemNum: row.orig_item_num,
            trialType: row.trial_type,
            image: ((_a = row === null || row === void 0 ? void 0 : row.image) === null || _a === void 0 ? void 0 : _a.includes(',')) ? row.image.replace(' ', '').split(',') : row === null || row === void 0 ? void 0 : row.image,
            timeLimit: row.time_limit,
            answer: (0, toNumber_1.default)(row.answer) || row.answer,
            assessmentStage: row.assessment_stage,
            chanceLevel: (0, toNumber_1.default)(row.chance_level),
            itemId: row.item_id,
            itemUid: row.item_uid,
            distractors: (() => {
                if (row.task === 'roar-inference') {
                    return row.response_alternatives.split(',').map((alt) => alt.replace(/"/g, ''));
                }
                else if (row.task === 'child-survey') {
                    return (0, childSurveyResponses_1.getChildSurveyResponses)();
                }
                else {
                    return containsLettersOrSlash(row.response_alternatives) ||
                        (row.task === 'adult-reasoning' && row.response_alternatives.includes(';'))
                        ? row.response_alternatives.split(',')
                        : (0, stringToNumArray_1.stringToNumberArray)(row.response_alternatives);
                }
            })(),
            audioFile: ((_b = row.audio_file) === null || _b === void 0 ? void 0 : _b.includes(',')) ? row.audio_file.split(',') : row.audio_file,
            // difficulty must be undefined to avoid running cat
            difficulty: (0, taskStore_1.taskStore)().runCat ? parseFloat(row.d || row.difficulty) : NaN,
            randomize: row.randomize,
            trialNumber: row.trial_num,
            downex: ((_c = row.downex) === null || _c === void 0 ? void 0 : _c.toUpperCase()) === 'TRUE',
            storyGroup: (0, toNumber_1.default)(row.story_group),
        };
        if (row.task === 'same-different-selection') {
            newRow.requiredSelections = (0, toNumber_1.default)(row.required_selections);
        }
        if (row.task === 'Mental Rotation') {
            newRow.item = (0, camelize_1.camelize)(newRow.item);
            newRow.answer = (0, camelize_1.camelize)(newRow.answer);
            newRow.distractors = newRow.distractors.map((choice) => (0, camelize_1.camelize)(choice));
        }
        if (row.task === 'same-different-selection') {
            newRow.requiredSelections = parseInt(row.required_selections);
        }
        let currentTrialType = newRow.trialType;
        if (currentTrialType !== currTrialTypeBlock) {
            currTrialTypeBlock = currentTrialType;
            currPracticeAmount = 0;
        }
        // Add cat values for clowder
        if ((0, shouldUseClowder_1.shouldUseClowder)()) {
            // catZeta columns always includes a period delimiter
            const catZetas = Object.fromEntries(Object.entries(row).filter(([key]) => key.includes('.')));
            Object.assign(newRow, catZetas);
        }
        if (newRow.downex) {
            // Add to downex corpus
            downexData.push(newRow);
            totalDownexTrials += 1;
        }
        else {
            // Add to stimulus corpus
            stimulusData.push(newRow);
            totalTrials += 1;
        }
    });
    (0, taskStore_1.taskStore)('blockThresholds', blockThresholds.sort((a, b) => a - b));
    // Only shuffle if unnormed scoring is enabled. Otherwise, Clowder handles the randomization.
    if (task === 'roar-inference' && !(0, taskStore_1.taskStore)().scoringVersion) {
        const inferenceNumStories = (0, taskStore_1.taskStore)().inferenceNumStories;
        const numItemsPerStory = (0, taskStore_1.taskStore)().stimulusBlocks;
        const notStoryTypes = ['introduction', 'practice'];
        stimulusData = (0, shuffleRoarInferenceStories_1.shuffleStories)(stimulusData, inferenceNumStories, 'itemId', notStoryTypes, numItemsPerStory);
        return;
    }
    if (!sequentialStimulus) {
        stimulusData = (0, randomizeStimulusBlocks_1.shuffleStimulusTrials)(stimulusData);
        if (downexData.length > 0) {
            downexData = (0, randomizeStimulusBlocks_1.shuffleStimulusTrials)(downexData);
        }
    }
};
const getCorpus = async (config, isDev) => {
    var _a;
    const { corpus, task, sequentialStimulus } = config;
    // Temporarily keep these here for backward compatibility
    /*const corpusLocation = {
      egmaMath: `https://storage.googleapis.com/${task}/shared/corpora/${corpus}.csv`,
      matrixReasoning: `https://storage.googleapis.com/${task}/shared/corpora/${corpus}.csv`,
      mentalRotation: `https://storage.googleapis.com/${task}/shared/corpora/${corpus}.csv`,
      sameDifferentSelection: `https://storage.googleapis.com/${task}/shared/corpora/${corpus}.csv`,
      trog: `https://storage.googleapis.com/roar-syntax/shared/corpora/${corpus}.csv`,
      theoryOfMind: `https://storage.googleapis.com/${task}/shared/corpora/${corpus}.csv`,
      vocab: `https://storage.googleapis.com/vocab-test/shared/corpora/${corpus}.csv`,
      roarInference: `https://storage.googleapis.com/roar-inference/en/corpora/${corpus}.csv`,
      adultReasoning: `https://storage.googleapis.com/egma-math/shared/corpora/${corpus}.csv`,
    };*/
    const bucketName = (0, getBucketName_1.getBucketName)(task, isDev, 'corpus');
    let corpusUrl = `https://storage.googleapis.com/${bucketName}/${corpus}.csv?alt=media`;
    // Default corpuses are CAT and norm compatible (v1)
    if (task === 'roar-inference') {
        // ROAR buckets are organized by base language code (en, es, de), not the full locale
        const language = ((_a = config.language) !== null && _a !== void 0 ? _a : 'en').split('-')[0];
        corpusUrl = `https://storage.googleapis.com/roar-inference/${language}/corpora/${corpus}.csv`;
    }
    else if (task === 'trog') {
        corpusUrl = `https://storage.googleapis.com/roar-syntax/shared/corpora/${corpus}.csv`;
    }
    function downloadCSV(url) {
        return new Promise((resolve, reject) => {
            papaparse_1.default.parse(url, {
                download: true,
                header: true,
                skipEmptyLines: true,
                complete: function (results) {
                    transformCSV(results.data, sequentialStimulus, task);
                    resolve(results.data);
                },
                error: function (error) {
                    reject(error);
                },
            });
        });
    }
    async function parseCSVs(urls) {
        const promises = urls.map((url, i) => downloadCSV(url));
        return Promise.all(promises);
    }
    async function fetchData() {
        // const urls = [corpusLocation[dashToCamelCase(task) as keyof typeof corpusLocation]];
        const urls = [corpusUrl];
        try {
            await parseCSVs(urls);
            (0, taskStore_1.taskStore)('totalTrials', totalTrials);
            (0, taskStore_1.taskStore)('totalDownexTrials', totalDownexTrials);
        }
        catch (error) {
            console.error('Error:', error);
        }
    }
    await fetchData();
    const csvTransformed = {
        stimulus: stimulusData, // previously shuffled by shuffleStimulusTrials
        downex: downexData,
    };
    (0, taskStore_1.taskStore)('corpora', csvTransformed);
};
exports.getCorpus = getCorpus;
